import React from 'react';
import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo
} from 'react';
import OrgService from '../services/org.service';
import { useAuth } from './AuthContext';
import VehicleService from 'services/vehicle.service';

const AppContext = createContext(null);

export const ApplicationProvider = ({ children }) => {
    const { user } = useAuth();
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [orgs, setOrgs] = useState([]);
    const [orgsLoading, setOrgsLoading] = useState(false);

    const isSuperAdmin = useMemo(() => {
        const perms = user?.permissions;
        return Array.isArray(perms) && perms.includes('super_admin');
    }, [user]);

    const fetchStates = useCallback(async () => {
        if (!user) return;
        try {
            setLoading(true);
            const data = await VehicleService.getSupportedCountries();
            setStates(data);
        } catch (e) {
            console.error('Failed to fetch countries', e);
        } finally {
            setLoading(false);
        }
    }, [user]);

    const fetchOrgs = useCallback(async () => {
        if (!user || !isSuperAdmin) return;

        try {
            setOrgsLoading(true);
            const res = await OrgService.getOrgs();
            setOrgs(res ?? []);
        } catch (e) {
            console.error('Failed to fetch organizations', e);
            setOrgs([]);
        } finally {
            setOrgsLoading(false);
        }
    }, [isSuperAdmin, user]);

    useEffect(() => {
        fetchOrgs();
    }, [fetchOrgs]);

    useEffect(() => {
        fetchStates();
    }, [fetchStates]);

    return (
        <AppContext.Provider value={{ loading, orgs, orgsLoading, states }}>
            {children}
        </AppContext.Provider>
    );
};

export function useApp() {
    const ctx = useContext(AppContext);
    if (!ctx) {
        throw new Error('useApp must be used inside AppProvider');
    }
    return ctx;
}
